import React, { forwardRef } from 'react';
import { clsx } from 'clsx';
import { XMarkIcon } from '@heroicons/react/20/solid';

/**
 * Badge Component
 *
 * A polished badge/chip component for status indicators,
 * tags, counts, and more.
 *
 * @example
 * <Badge>Default</Badge>
 * <Badge variant="success" dot>Active</Badge>
 * <Badge variant="error" size="lg">Critical</Badge>
 */

const badgeVariants = {
  default: 'bg-gray-100 text-gray-700 border-gray-200',
  primary: 'bg-wrg-blue-100 text-wrg-blue-700 border-wrg-blue-200',
  secondary: 'bg-gray-100 text-gray-600 border-gray-200',
  success: 'bg-success-100 text-success-700 border-success-200',
  warning: 'bg-warning-100 text-warning-700 border-warning-200',
  error: 'bg-error-100 text-error-700 border-error-200',
  info: 'bg-info-100 text-info-700 border-info-200',
  // Solid variants
  'primary-solid': 'bg-wrg-blue-500 text-white border-wrg-blue-500',
  'success-solid': 'bg-success-700 text-white border-success-700',
  'warning-solid': 'bg-warning-700 text-white border-warning-700',
  'error-solid': 'bg-error-600 text-white border-error-600',
  'info-solid': 'bg-info-600 text-white border-info-600',
  // Outline variants
  'primary-outline': 'bg-transparent text-wrg-blue-600 border-wrg-blue-300',
  'success-outline': 'bg-transparent text-success-700 border-success-300',
  'warning-outline': 'bg-transparent text-warning-700 border-warning-300',
  'error-outline': 'bg-transparent text-error-600 border-error-300',
  'info-outline': 'bg-transparent text-info-600 border-info-300',
};

const badgeSizes = {
  xs: 'text-xxs px-1.5 py-0.5 gap-1',
  sm: 'text-xs px-2 py-0.5 gap-1',
  md: 'text-xs px-2.5 py-1 gap-1.5',
  lg: 'text-sm px-3 py-1 gap-1.5',
};

const dotColors = {
  default: 'bg-gray-400',
  primary: 'bg-wrg-blue-500',
  secondary: 'bg-gray-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  error: 'bg-error-500',
  info: 'bg-info-500',
  'primary-solid': 'bg-white',
  'success-solid': 'bg-white',
  'warning-solid': 'bg-white',
  'error-solid': 'bg-white',
  'info-solid': 'bg-white',
  'primary-outline': 'bg-wrg-blue-500',
  'success-outline': 'bg-success-500',
  'warning-outline': 'bg-warning-500',
  'error-outline': 'bg-error-500',
  'info-outline': 'bg-info-500',
};

const dotSizes = {
  xs: 'size-1',
  sm: 'size-1.5',
  md: 'size-1.5',
  lg: 'size-2',
};

const Badge = forwardRef(
  (
    {
      children,
      variant = 'default',
      size = 'sm',
      dot = false,
      pulse = false,
      icon,
      removable = false,
      onRemove,
      rounded = true,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <span
        ref={ref}
        className={clsx(
          'inline-flex items-center font-medium',
          'border',
          rounded ? 'rounded-full' : 'rounded-md',
          badgeVariants[variant],
          badgeSizes[size],
          className
        )}
        {...props}
      >
        {/* Status dot */}
        {dot && (
          <span className="relative flex-shrink-0">
            <span
              className={clsx(
                'rounded-full',
                dotSizes[size],
                dotColors[variant]
              )}
            />
            {pulse && (
              <span
                className={clsx(
                  'absolute inset-0 rounded-full animate-ping opacity-75',
                  dotColors[variant]
                )}
              />
            )}
          </span>
        )}

        {/* Icon - can be a component or element */}
        {icon && (
          <span
            className={clsx(
              'flex-shrink-0 flex items-center justify-center',
              size === 'xs' ? 'size-3' : size === 'sm' ? 'size-3.5' : 'size-4'
            )}
          >
            {React.isValidElement(icon) ? icon : React.createElement(icon, { className: 'w-full h-full' })}
          </span>
        )}

        {/* Content */}
        <span className="whitespace-nowrap">{children}</span>

        {/* Remove button */}
        {removable && (
          <button
            type="button"
            onClick={onRemove}
            className={clsx(
              'flex-shrink-0 -mr-1 ml-0.5',
              'rounded-full p-0.5',
              'hover:bg-black/10 focus:bg-black/10',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
              'transition-colors duration-150'
            )}
            aria-label="Remove"
          >
            <XMarkIcon
              className={clsx(
                size === 'xs' ? 'size-2.5' : size === 'sm' ? 'size-3' : 'size-3.5'
              )}
            />
          </button>
        )}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

/**
 * CountBadge Component
 *
 * For displaying numeric counts
 */
const CountBadge = forwardRef(
  (
    {
      count,
      max = 99,
      variant = 'primary',
      size = 'sm',
      showZero = false,
      className,
      ...props
    },
    ref
  ) => {
    if (count === 0 && !showZero) return null;

    const displayCount = count > max ? `${max}+` : count;

    const countSizes = {
      xs: 'text-xxs min-w-[16px] h-4 px-1',
      sm: 'text-xs min-w-[18px] h-[18px] px-1.5',
      md: 'text-xs min-w-[20px] h-5 px-1.5',
      lg: 'text-sm min-w-[24px] h-6 px-2',
    };

    const countVariants = {
      primary: 'bg-wrg-blue-500 text-white',
      secondary: 'bg-gray-500 text-white',
      success: 'bg-success-700 text-white',
      warning: 'bg-warning-700 text-white',
      error: 'bg-error-600 text-white',
      info: 'bg-info-600 text-white',
      muted: 'bg-gray-200 text-gray-600',
    };

    return (
      <span
        ref={ref}
        className={clsx(
          'inline-flex items-center justify-center',
          'font-semibold rounded-full',
          countSizes[size],
          countVariants[variant],
          className
        )}
        {...props}
      >
        {displayCount}
      </span>
    );
  }
);

CountBadge.displayName = 'CountBadge';

/**
 * StatusDot Component
 *
 * Simple status indicator dot
 */
const StatusDot = forwardRef(
  (
    {
      status = 'default', // 'online', 'offline', 'away', 'busy', or any variant color
      size = 'md',
      pulse = false,
      className,
      ...props
    },
    ref
  ) => {
    const statusColors = {
      online: 'bg-success-500',
      offline: 'bg-gray-400',
      away: 'bg-warning-500',
      busy: 'bg-error-500',
      default: 'bg-gray-400',
      primary: 'bg-wrg-blue-500',
      success: 'bg-success-500',
      warning: 'bg-warning-500',
      error: 'bg-error-500',
      info: 'bg-info-500',
    };

    const sizes = {
      xs: 'size-1.5',
      sm: 'size-2',
      md: 'size-2.5',
      lg: 'size-3',
    };

    return (
      <span
        ref={ref}
        className={clsx('relative inline-flex', className)}
        {...props}
      >
        <span
          className={clsx(
            'rounded-full',
            sizes[size],
            statusColors[status]
          )}
        />
        {pulse && (
          <span
            className={clsx(
              'absolute inset-0 rounded-full animate-ping opacity-75',
              statusColors[status]
            )}
          />
        )}
        <span className="sr-only">{status}</span>
      </span>
    );
  }
);

StatusDot.displayName = 'StatusDot';

/**
 * Chip Component
 *
 * Interactive chips for selections/filters
 */
const Chip = forwardRef(
  (
    {
      children,
      selected = false,
      disabled = false,
      removable = false,
      onClick,
      onRemove,
      icon,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        role="group"
        className={clsx(
          'inline-flex items-center gap-1.5',
          'pl-3 py-1.5',
          removable ? 'pr-1.5' : 'pr-3',
          'text-sm font-medium',
          'rounded-full border',
          'transition-colors duration-150',
          selected
            ? 'bg-wrg-blue-500 text-white border-wrg-blue-500'
            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50',
          disabled && 'opacity-50',
          className
        )}
        {...props}
      >
        <button
          ref={ref}
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={clsx(
            'inline-flex items-center gap-1.5 rounded-full',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-wrg-blue-500 focus-visible:ring-offset-1',
            disabled && 'cursor-not-allowed'
          )}
        >
          {icon && (
            <span className="size-4 flex-shrink-0 flex items-center justify-center">
              {React.isValidElement(icon) ? icon : React.createElement(icon, { className: 'w-full h-full' })}
            </span>
          )}
          <span className="truncate">{children}</span>
        </button>
        {removable && (
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            aria-label="Remove"
            className={clsx(
              'flex-shrink-0 p-0.5 rounded-full',
              'hover:bg-black/10',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-wrg-blue-500 focus-visible:ring-offset-1',
              disabled && 'cursor-not-allowed'
            )}
          >
            <XMarkIcon className="size-3.5" />
          </button>
        )}
      </div>
    );
  }
);

Chip.displayName = 'Chip';

/**
 * ChipGroup Component
 *
 * Container for multiple chips
 */
const ChipGroup = forwardRef(
  ({ children, className, wrap = true, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'flex items-center gap-2',
          wrap && 'flex-wrap',
          className
        )}
        role="group"
        {...props}
      >
        {children}
      </div>
    );
  }
);

ChipGroup.displayName = 'ChipGroup';

/**
 * BadgeGroup Component
 *
 * Container for multiple badges
 */
const BadgeGroup = forwardRef(
  ({ children, className, max, ...props }, ref) => {
    const childArray = React.Children.toArray(children);
    const visibleChildren = max ? childArray.slice(0, max) : childArray;
    const remainingCount = max ? childArray.length - max : 0;

    return (
      <div
        ref={ref}
        className={clsx('inline-flex items-center gap-1.5 flex-wrap', className)}
        {...props}
      >
        {visibleChildren}
        {remainingCount > 0 && (
          <Badge variant="secondary" size="sm">
            +{remainingCount}
          </Badge>
        )}
      </div>
    );
  }
);

BadgeGroup.displayName = 'BadgeGroup';

export { Badge, CountBadge, StatusDot, Chip, ChipGroup, BadgeGroup };
export default Badge;
